import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Lock, ArrowRight, Briefcase, Inbox, BookOpenCheck, FileText, Sparkles, Loader2 } from "lucide-react";

/**
 * EnterpriseGate
 * ----------------------------------------------------------------
 * Wrap any page that requires the Business plan. If the active
 * tenant isn't on Business, renders an upsell card instead of the
 * page's children. Mirrors the backend `require_enterprise_tenant`
 * dep so users can't bypass via deep-link.
 */
export default function EnterpriseGate({ tenant, title, subtitle, children, testid }) {
  const isBusiness = ["pro", "business"].includes(tenant?.plan);
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const alreadyTrialed = (tenant?.plan_trials_used || []).includes("business");

  if (isBusiness) return children;

  const startTrial = async () => {
    setBusy(true);
    try {
      await api.post("/billing/start-trial", { plan: "business" });
      await refreshMe();
      toast.success("Business trial started · 7 days, no card required");
      navigate(title === "Review queue" ? "/app/queue" : "/app/clients");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start trial");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} testid={`${testid}-header`} />
      <PageBody>
        <div className="ll-card-flat p-10 max-w-2xl mx-auto text-center" data-testid={`${testid}-locked`}>
          <div
            className="w-12 h-12 mx-auto mb-4 grid place-items-center"
            style={{ background: "var(--ll-mint)" }}
          >
            <Lock size={20} className="text-[var(--ll-ink)]" />
          </div>
          <div className="font-display font-bold text-2xl mb-2">
            Available on the Business plan
          </div>
          <p className="text-sm text-[var(--ll-ink-soft)] max-w-md mx-auto mb-6 leading-relaxed">
            Manage multiple client companies under one login. Switch workspaces,
            review queues across every client, and export firm-level summaries —
            all from the same dashboard.
          </p>

          <ul className="grid sm:grid-cols-2 gap-2 mb-7 max-w-md mx-auto text-left">
            {[
              [Briefcase, "Unlimited client workspaces"],
              [Inbox, "Cross-tenant review queue"],
              [BookOpenCheck, "Per-client sparklines + KPIs"],
              [FileText, "Firm-level PDF / XLSX exports"],
            ].map(([Icon, label]) => (
              <li key={label} className="flex items-center gap-2 text-xs text-[var(--ll-ink-soft)] border px-3 py-2" style={{ borderColor: "var(--ll-border)" }}>
                <Icon size={12} className="text-[var(--ll-ink)] shrink-0" />
                {label}
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-md mx-auto">
            {!alreadyTrialed && (
              <button
                type="button"
                onClick={startTrial}
                disabled={busy}
                data-testid={`${testid}-trial-cta`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm font-medium transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {busy ? "Starting…" : "Start 7-day Business trial"}
              </button>
            )}
            <Link
              to="/app/billing"
              data-testid={`${testid}-upgrade-cta`}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium transition-colors border"
              style={alreadyTrialed
                ? { background: "var(--ll-ink)", color: "white", borderColor: "var(--ll-ink)" }
                : { borderColor: "var(--ll-border)", color: "var(--ll-ink)" }}
            >
              {alreadyTrialed ? "Upgrade to Business" : "See pricing"} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)]">
            {alreadyTrialed
              ? "Your free trial has been used · Upgrade to keep these features"
              : `Current plan · ${tenant?.plan || "trial"} · No credit card required`}
          </div>
        </div>
      </PageBody>
    </>
  );
}
